import React, { useState } from 'react';
import axios from 'axios';
import { Plus, Trash2, Save, Camera, Star } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMBED_TYPES = [
  { value: 'youtube', label: 'YouTube' },
  { value: 'vimeo', label: 'Vimeo' },
  { value: 'spotify', label: 'Spotify' },
  { value: 'soundcloud', label: 'SoundCloud' },
  { value: 'map', label: 'Google Maps' },
  { value: 'custom', label: 'Custom Embed' },
];

const ContentTab = ({ profile, setProfile, showMsg }) => {
  const [embeds, setEmbeds] = useState(profile.embeds || []);
  const [showcase, setShowcase] = useState(profile.showcase || []);
  const [testimonials, setTestimonials] = useState(profile.testimonials || []);
  const [savingEmbeds, setSavingEmbeds] = useState(false);
  const [savingShowcase, setSavingShowcase] = useState(false);
  const [savingTestimonials, setSavingTestimonials] = useState(false);

  const addEmbed = () => setEmbeds([...embeds, { id: Date.now().toString(), title: '', embed_type: 'youtube', embed_url: '', order: embeds.length }]);
  const removeEmbed = (idx) => setEmbeds(embeds.filter((_, i) => i !== idx));
  const updateEmbed = (idx, field, value) => setEmbeds(embeds.map((e, i) => i === idx ? { ...e, [field]: value } : e));

  const saveEmbeds = async () => {
    setSavingEmbeds(true);
    try {
      await axios.put(`${API}/profile/embeds`, { embeds });
      setProfile(prev => ({ ...prev, embeds }));
      showMsg('Embeds saved');
    } catch (err) { showMsg(err.response?.data?.detail || 'Failed to save'); }
    finally { setSavingEmbeds(false); }
  };

  const addShowcaseItem = () => setShowcase([...showcase, { id: Date.now().toString(), title: '', description: '', image_url: '', price: '', link_url: '', order: showcase.length }]);
  const removeShowcaseItem = (idx) => setShowcase(showcase.filter((_, i) => i !== idx));
  const updateShowcaseItem = (idx, field, value) => setShowcase(showcase.map((s, i) => i === idx ? { ...s, [field]: value } : s));

  const uploadShowcaseImage = async (idx, e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await axios.post(`${API}/profile/showcase/image`, fd);
      updateShowcaseItem(idx, 'image_url', res.data.image_url);
      showMsg('Image uploaded');
    } catch (err) { showMsg(err.response?.data?.detail || 'Upload failed'); }
  };

  const saveShowcase = async () => {
    setSavingShowcase(true);
    try {
      await axios.put(`${API}/profile/showcase`, { showcase });
      setProfile(prev => ({ ...prev, showcase }));
      showMsg('Showcase saved');
    } catch (err) { showMsg(err.response?.data?.detail || 'Failed to save'); }
    finally { setSavingShowcase(false); }
  };

  const addTestimonial = () => setTestimonials([...testimonials, { id: Date.now().toString(), name: '', role: '', text: '', rating: 5, order: testimonials.length }]);
  const removeTestimonial = (idx) => setTestimonials(testimonials.filter((_, i) => i !== idx));
  const updateTestimonial = (idx, field, value) => setTestimonials(testimonials.map((t, i) => i === idx ? { ...t, [field]: value } : t));

  const saveTestimonials = async () => {
    setSavingTestimonials(true);
    try {
      await axios.put(`${API}/profile/testimonials`, { testimonials });
      setProfile(prev => ({ ...prev, testimonials }));
      showMsg('Testimonials saved');
    } catch (err) { showMsg(err.response?.data?.detail || 'Failed to save'); }
    finally { setSavingTestimonials(false); }
  };

  const isPro = profile.plan !== 'free';

  return (
    <div data-testid="content-tab">
      <h2 className="font-heading text-2xl font-normal text-gray-900 mb-2">Rich Content</h2>
      <p className="text-sm text-gray-500 mb-8">Add videos, music, and products to your card</p>

      {/* Embeds Section */}
      <div className="mb-10">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-gray-700">Embedded Media</h3>
          <span className="text-xs text-gray-400">{embeds.length}{!isPro ? '/1' : ''}</span>
        </div>
        <p className="text-xs text-gray-400 mb-4">Add YouTube videos, Spotify tracks, or maps to your card</p>
        
        <div className="space-y-4 mb-4">
          {embeds.map((embed, idx) => (
            <div key={embed.id} className="bg-white border border-gray-200 rounded-xl p-4" data-testid={`embed-item-${idx}`}>
              <div className="flex gap-3 mb-3">
                <select value={embed.embed_type} onChange={e => updateEmbed(idx, 'embed_type', e.target.value)}
                  className="bg-gray-50 border border-gray-200 rounded-lg text-gray-700 px-3 py-2 text-sm outline-none w-32 focus:ring-2 focus:ring-indigo-100">
                  {EMBED_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <input value={embed.title} onChange={e => updateEmbed(idx, 'title', e.target.value)} placeholder="Title (optional)"
                  className="flex-1 bg-white border border-gray-200 rounded-lg text-gray-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-100" />
                <button onClick={() => removeEmbed(idx)} className="text-gray-300 hover:text-red-500 transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>
              <input value={embed.embed_url} onChange={e => updateEmbed(idx, 'embed_url', e.target.value)}
                placeholder={embed.embed_type === 'youtube' ? 'https://youtube.com/watch?v=...' : embed.embed_type === 'spotify' ? 'https://open.spotify.com/track/...' : 'Paste URL or embed code'}
                className="w-full bg-white border border-gray-200 rounded-lg text-gray-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-100" />
            </div>
          ))}
        </div>
        
        <div className="flex gap-3">
          <button onClick={addEmbed} disabled={!isPro && embeds.length >= 1}
            className="flex items-center gap-2 border border-dashed border-gray-300 text-gray-500 hover:text-indigo-600 hover:border-indigo-300 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="add-embed-btn">
            <Plus className="w-4 h-4" /> Add Embed {!isPro && embeds.length >= 1 && <span className="text-xs text-amber-500">(Pro)</span>}
          </button>
          {embeds.length > 0 && (
            <button onClick={saveEmbeds} disabled={savingEmbeds}
              className="bg-gray-900 text-white px-5 py-2.5 rounded-lg font-semibold text-sm hover:bg-black transition-colors disabled:opacity-50 flex items-center gap-2">
              <Save className="w-4 h-4" /> {savingEmbeds ? 'Saving...' : 'Save'}
            </button>
          )}
        </div>
      </div>

      {/* Showcase Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-gray-700">Product / Service Showcase</h3>
          <span className="text-xs text-gray-400">{showcase.length}{!isPro ? '/2' : ''}</span>
        </div>
        <p className="text-xs text-gray-400 mb-4">Highlight your products, services, or portfolio items</p>
        
        <div className="space-y-4 mb-4">
          {showcase.map((item, idx) => (
            <div key={item.id} className="bg-white border border-gray-200 rounded-xl p-4" data-testid={`showcase-item-${idx}`}>
              <div className="flex gap-4">
                <div className="w-24 h-24 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0 relative group">
                  {item.image_url ? (
                    <img src={item.image_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Camera className="w-6 h-6 text-gray-300" />
                    </div>
                  )}
                  <label className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center cursor-pointer transition-opacity">
                    <Camera className="w-5 h-5 text-white" />
                    <input type="file" accept="image/*" onChange={e => uploadShowcaseImage(idx, e)} className="hidden" />
                  </label>
                </div>
                
                <div className="flex-1 space-y-2">
                  <div className="flex gap-2">
                    <input value={item.title} onChange={e => updateShowcaseItem(idx, 'title', e.target.value)} placeholder="Title"
                      className="flex-1 bg-white border border-gray-200 rounded-lg text-gray-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-100" />
                    <input value={item.price} onChange={e => updateShowcaseItem(idx, 'price', e.target.value)} placeholder="$99"
                      className="w-24 bg-white border border-gray-200 rounded-lg text-gray-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-100" />
                    <button onClick={() => removeShowcaseItem(idx)} className="text-gray-300 hover:text-red-500 transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <input value={item.description} onChange={e => updateShowcaseItem(idx, 'description', e.target.value)} placeholder="Short description"
                    className="w-full bg-white border border-gray-200 rounded-lg text-gray-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-100" />
                  <input value={item.link_url} onChange={e => updateShowcaseItem(idx, 'link_url', e.target.value)} placeholder="https://link-to-product.com"
                    className="w-full bg-white border border-gray-200 rounded-lg text-gray-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-100" />
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="flex gap-3">
          <button onClick={addShowcaseItem} disabled={!isPro && showcase.length >= 2}
            className="flex items-center gap-2 border border-dashed border-gray-300 text-gray-500 hover:text-indigo-600 hover:border-indigo-300 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="add-showcase-btn">
            <Plus className="w-4 h-4" /> Add Item {!isPro && showcase.length >= 2 && <span className="text-xs text-amber-500">(Pro)</span>}
          </button>
          {showcase.length > 0 && (
            <button onClick={saveShowcase} disabled={savingShowcase}
              className="bg-gray-900 text-white px-5 py-2.5 rounded-lg font-semibold text-sm hover:bg-black transition-colors disabled:opacity-50 flex items-center gap-2">
              <Save className="w-4 h-4" /> {savingShowcase ? 'Saving...' : 'Save'}
            </button>
          )}
        </div>
      </div>

      {/* Testimonials Section */}
      <div className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-gray-700">Testimonials</h3>
          <span className="text-xs text-gray-400">{testimonials.length}{!isPro ? '/3' : ''}</span>
        </div>
        <p className="text-xs text-gray-400 mb-4">Add reviews and testimonials to build trust</p>

        <div className="space-y-4 mb-4">
          {testimonials.map((item, idx) => (
            <div key={item.id} className="bg-white border border-gray-200 rounded-xl p-4" data-testid={`testimonial-item-${idx}`}>
              <div className="flex items-start gap-3 mb-3">
                <div className="flex-1 space-y-2">
                  <div className="flex gap-2">
                    <input value={item.name} onChange={e => updateTestimonial(idx, 'name', e.target.value)} placeholder="Person's name"
                      className="flex-1 bg-white border border-gray-200 rounded-lg text-gray-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-100" />
                    <input value={item.role} onChange={e => updateTestimonial(idx, 'role', e.target.value)} placeholder="Role / Company"
                      className="flex-1 bg-white border border-gray-200 rounded-lg text-gray-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-100" />
                    <button onClick={() => removeTestimonial(idx)} className="text-gray-300 hover:text-red-500 transition-colors mt-2"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <textarea value={item.text} onChange={e => updateTestimonial(idx, 'text', e.target.value)} placeholder="What did they say?"
                    rows={2} className="w-full bg-white border border-gray-200 rounded-lg text-gray-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-100 resize-none" />
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map(s => (
                      <button key={s} onClick={() => updateTestimonial(idx, 'rating', s)} data-testid={`testimonial-${idx}-star-${s}`}>
                        <Star className={`w-4 h-4 transition-colors ${s <= item.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-200'}`} />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-3">
          <button onClick={addTestimonial} disabled={!isPro && testimonials.length >= 3}
            className="flex items-center gap-2 border border-dashed border-gray-300 text-gray-500 hover:text-indigo-600 hover:border-indigo-300 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="add-testimonial-btn">
            <Plus className="w-4 h-4" /> Add Testimonial {!isPro && testimonials.length >= 3 && <span className="text-xs text-amber-500">(Pro)</span>}
          </button>
          {testimonials.length > 0 && (
            <button onClick={saveTestimonials} disabled={savingTestimonials}
              className="bg-gray-900 text-white px-5 py-2.5 rounded-lg font-semibold text-sm hover:bg-black transition-colors disabled:opacity-50 flex items-center gap-2"
              data-testid="save-testimonials-btn">
              <Save className="w-4 h-4" /> {savingTestimonials ? 'Saving...' : 'Save'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContentTab;
